import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth interceptor — adds token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// PO APIs
export const uploadPOs = (files) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  return api.post('/po/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getAllPOs = () => api.get('/po');
export const getPO = (id) => api.get(`/po/${id}`);
export const updatePO = (id, data) => api.put(`/po/${id}`, data);
export const deletePO = (id) => api.delete(`/po/${id}`);
export const updatePOStatus = (id, status) => api.put(`/po/${id}/status`, { status });

// Inventory APIs
export const uploadInventory = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/inventory/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getInventory = (brand = null) => {
  const params = brand && brand !== 'all' ? { brand } : {};
  return api.get('/inventory', { params });
};
export const getBrands = () => api.get('/brands');
export const downloadInventory = () => api.get('/inventory/download', { responseType: 'blob' });
export const getInventoryAuditLog = (modelName) => api.get(`/inventory/audit-log/${encodeURIComponent(modelName)}`);
export const updateInventoryItem = (srNo, data) => api.put(`/inventory/${srNo}`, data);

// Shopify APIs
export const syncShopify = () => api.post('/shopify/sync');
export const configureShopify = (config) => api.post('/shopify/configure', config);

// Invoice APIs
export const previewInvoice = (poId) => api.post(`/invoice/preview/${poId}`);
export const generateInvoice = (poId) => api.post(`/invoice/generate/${poId}`);
export const getAllInvoices = () => api.get('/invoice');
export const getInvoicePDF = (invoiceId) => 
  api.get(`/invoice/${invoiceId}/pdf`, { responseType: 'blob' });
export const updateInvoiceStatus = (id, status) => api.put(`/invoice/${id}/status`, { status });

// Bulk Invoice APIs
export const bulkGenerateInvoices = (data) => api.post('/po/bulk/generate', data);
export const bulkClosePOs = (data) => api.post('/po/bulk/close', data);
export const getInvoiceBatches = () => api.get('/invoice/batches');
export const getBatchInvoicePDF = (batchId) => api.get(`/invoice/batch/${batchId}/pdf`, { responseType: 'blob' });
export const getBulkInvoicePDF = () => api.get('/invoice/bulk/pdf', { responseType: 'blob' });
export const getBulkInvoiceZip = () => api.get('/invoice/bulk/zip', { responseType: 'blob' });
export const getSelectedInvoicesPDF = (invoiceIds) => api.post('/invoice/selected/pdf', { invoice_ids: invoiceIds }, { responseType: 'blob' });
export const getSelectedInvoicesZip = (invoiceIds) => api.post('/invoice/selected/zip', { invoice_ids: invoiceIds }, { responseType: 'blob' });

// AWB & Courier
export const updateInvoiceAWB = (invoiceId, data) => api.put(`/invoice/${invoiceId}/awb`, data);
export const getCourierSettings = () => api.get('/courier-settings');

// Email Dispatch
export const sendDispatchEmails = (data) => api.post('/dispatch/send-emails', data);

// Analytics APIs
export const getStoreSales = () => api.get('/analytics/store-sales');
export const getStoreTopSkus = (storeName) => api.get(`/analytics/store-top-skus/${encodeURIComponent(storeName)}`);
export const getAnalyticsOverview = () => api.get('/analytics/overview');

// Packaging APIs
export const uploadPackaging = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/packaging/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getPackaging = (category = null) => {
  const params = category && category !== 'all' ? { category } : {};
  return api.get('/packaging', { params });
};
export const getPackagingAlerts = () => api.get('/packaging/alerts');
export const getPackagingStats = () => api.get('/packaging/stats');
export const downloadPackaging = () => api.get('/packaging/download', { responseType: 'blob' });
export const updatePackagingItem = (srNo, data) => api.put(`/packaging/${srNo}`, data);
export const deletePackagingItem = (srNo) => api.delete(`/packaging/${srNo}`);

// Settings APIs
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const resetAllData = () => api.post('/settings/reset');

// Display Kits APIs
export const getDisplayKitSettings = () => api.get('/display-kits/settings');
export const updateDisplayKitSettings = (data) => api.put('/display-kits/settings', data);
export const getDisplayKitStats = () => api.get('/display-kits/stats');
export const distributeDisplayKits = (data) => api.post('/display-kits/distribute', data);
export const addDisplayKitStock = (quantity) => api.post(`/display-kits/add-stock?quantity=${quantity}`);
export const getDisplayKitHistory = () => api.get('/display-kits/history');

// Inventory Planning APIs
export const getInventoryPlanningStats = () => api.get('/inventory-planning/stats');
export const getInventoryPlanningSettings = () => api.get('/inventory-planning/settings');
export const updateInventoryPlanningSettings = (data) => api.put('/inventory-planning/settings', data);

// Store Directory APIs
export const getStores = () => api.get('/stores');
export const addStore = (data) => api.post('/stores', data);
export const updateStore = (storeName, data) => api.put(`/stores/${encodeURIComponent(storeName)}`, data);
export const deleteStore = (storeName) => api.delete(`/stores/${encodeURIComponent(storeName)}`);
export const syncStoresFromPOs = () => api.post('/stores/sync-from-pos');
export const syncStoresFromStock = () => api.post('/stores/sync-from-stock');

// Manual PO
export const createManualPO = (data) => api.post('/po/manual', data);

// ─── AP Brand APIs ──────────────────────────────────────────
// AP Settings
export const getAPSettings = () => api.get('/ap/settings');
export const updateAPSettings = (data) => api.put('/ap/settings', data);

// AP Inventory
export const getAPInventory = () => api.get('/ap/inventory');
export const getAPInventoryWithImages = () => api.get('/ap/inventory/with-image-flags');
export const uploadAPInventory = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/ap/inventory/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const updateAPInventoryItem = (srNo, data) => api.put(`/ap/inventory/${srNo}`, data);
export const uploadAPProductImage = (modelName, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/ap/inventory/${encodeURIComponent(modelName)}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const downloadAPInventory = () => api.get('/ap/inventory/download', { responseType: 'blob' });

// AP Tags
export const getAPTagSettings = () => api.get('/ap/tag-settings');
export const updateAPTagSettings = (data) => api.put('/ap/tag-settings', data);
export const getAPBoxTag = (srNo, importMonth) => `${process.env.REACT_APP_BACKEND_URL}/api/ap/inventory/${srNo}/box-tag?import_month=${encodeURIComponent(importMonth || '')}`;
export const getAPProductTag = (srNo) => `${process.env.REACT_APP_BACKEND_URL}/api/ap/inventory/${srNo}/product-tag`;
export const bulkAPBoxTags = (data) => api.post('/ap/inventory/bulk-box-tags', data, { responseType: 'blob' });
export const bulkAPProductTags = (data) => api.post('/ap/inventory/bulk-product-tags', data, { responseType: 'blob' });

// AP Product Image (lazy load)
export const getAPProductImage = (modelName) => api.get(`/ap/inventory/${encodeURIComponent(modelName)}/image`);

// AP PO
export const uploadAPPOs = (files) => { const fd = new FormData(); files.forEach(f => fd.append('files', f)); return api.post('/ap/po/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const getAPPOs = () => api.get('/ap/po');
export const deleteAPPO = (id) => api.delete(`/ap/po/${id}`);
export const createAPManualPO = (data) => api.post('/ap/po/manual', data);
export const updateAPPOStatus = (id, status) => api.put(`/ap/po/${id}/status`, { status });
export const bulkGenerateAPInvoices = (data) => api.post('/ap/po/bulk/generate', data);
export const bulkCloseAPPOs = (data) => api.post('/ap/po/bulk/close', data);

// AP Invoices
export const previewAPInvoice = (poId) => api.post(`/ap/invoice/preview/${poId}`);
export const generateAPInvoice = (poId) => api.post(`/ap/invoice/generate/${poId}`);
export const getAPInvoices = () => api.get('/ap/invoice');
export const getAPInvoicePDF = (id) => api.get(`/ap/invoice/${id}/pdf`, { responseType: 'blob' });
export const updateAPInvoiceStatus = (id, status) => api.put(`/ap/invoice/${id}/status`, { status });
export const cancelAPInvoice = (id) => api.post(`/ap/invoice/${id}/cancel`);
export const updateAPInvoiceAWB = (id, data) => api.put(`/ap/invoice/${id}/awb`, data);
export const getAPSelectedInvoicesPDF = (ids) => api.post('/ap/invoice/selected/pdf', { invoice_ids: ids }, { responseType: 'blob' });
export const getAPSelectedInvoicesZip = (ids) => api.post('/ap/invoice/selected/zip', { invoice_ids: ids }, { responseType: 'blob' });
export const getAPInvoiceBatches = () => api.get('/ap/invoice/batches');
export const getAPBatchInvoicePDF = (batchId) => api.get(`/ap/invoice/batch/${batchId}/pdf`, { responseType: 'blob' });

// AP Courier Settings
export const getAPCourierSettings = () => api.get('/ap/courier-settings');
export const updateAPCourierSettings = (data) => api.put('/ap/courier-settings', data);

// AP Stores
export const getAPStores = () => api.get('/ap/stores');
export const addAPStore = (data) => api.post('/ap/stores', data);
export const updateAPStore = (name, data) => api.put(`/ap/stores/${encodeURIComponent(name)}`, data);
export const deleteAPStore = (name) => api.delete(`/ap/stores/${encodeURIComponent(name)}`);
export const syncAPStoresFromPOs = () => api.post('/ap/stores/sync-from-pos');

// AP Revenue Analytics
export const getAPAnalyticsOverview = () => api.get('/ap/analytics/overview');
export const getAPAnalyticsStoreSales = (params) => api.get('/ap/analytics/store-sales', { params });
export const getAPAnalyticsStoreTopSkus = (store) => api.get(`/ap/analytics/store-top-skus/${encodeURIComponent(store)}`);


// AP Email
export const getAPStoreContacts = () => api.get('/ap/store-contacts');
export const sendAPDispatchEmails = (data) => api.post('/ap/dispatch/send-emails', data);

// AP Viewfinder
export const generateViewfinderLink = () => api.post('/ap/viewfinder/generate-link');
export const getViewfinderToken = () => api.get('/ap/viewfinder/token');
export const getPublicViewfinder = (token) => api.get(`/viewfinder/${token}`);

// AP Attributes
export const getAPAttributes = () => api.get('/ap/attributes');
export const uploadAPAttributes = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/ap/attributes/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const downloadAPAttributes = () => api.get('/ap/attributes/download', { responseType: 'blob' });
export const getPublicViewfinderAttributes = (token) => api.get(`/viewfinder/${token}/attributes`);
export const downloadPublicAttributes = (token) => api.get(`/viewfinder/${token}/attributes/download`, { responseType: 'blob' });


// ─── EXTREME Brand APIs ──────────────────────────────────────
// EXTREME Settings
export const getEXSettings = () => api.get('/extreme/settings');
export const updateEXSettings = (data) => api.put('/extreme/settings', data);

// EXTREME Inventory
export const getEXInventory = () => api.get('/extreme/inventory');
export const getEXInventoryWithImages = () => api.get('/extreme/inventory/with-image-flags');
export const uploadEXInventory = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/extreme/inventory/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const updateEXInventoryItem = (srNo, data) => api.put(`/extreme/inventory/${srNo}`, data);
export const uploadEXProductImage = (modelName, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/extreme/inventory/${encodeURIComponent(modelName)}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const downloadEXInventory = () => api.get('/extreme/inventory/download', { responseType: 'blob' });

// EXTREME Tags
export const getEXTagSettings = () => api.get('/extreme/tag-settings');
export const updateEXTagSettings = (data) => api.put('/extreme/tag-settings', data);
export const getEXBoxTag = (srNo, importMonth) => `${process.env.REACT_APP_BACKEND_URL}/api/extreme/inventory/${srNo}/box-tag?import_month=${encodeURIComponent(importMonth || '')}`;
export const getEXProductTag = (srNo) => `${process.env.REACT_APP_BACKEND_URL}/api/extreme/inventory/${srNo}/product-tag`;
export const bulkEXBoxTags = (data) => api.post('/extreme/inventory/bulk-box-tags', data, { responseType: 'blob' });
export const bulkEXProductTags = (data) => api.post('/extreme/inventory/bulk-product-tags', data, { responseType: 'blob' });

// EXTREME Product Image
export const getEXProductImage = (modelName) => api.get(`/extreme/inventory/${encodeURIComponent(modelName)}/image`);

// EXTREME PO
export const uploadEXPOs = (files) => { const fd = new FormData(); files.forEach(f => fd.append('files', f)); return api.post('/extreme/po/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); };
export const getEXPOs = () => api.get('/extreme/po');
export const deleteEXPO = (id) => api.delete(`/extreme/po/${id}`);
export const createEXManualPO = (data) => api.post('/extreme/po/manual', data);
export const updateEXPOStatus = (id, status) => api.put(`/extreme/po/${id}/status`, { status });
export const bulkGenerateEXInvoices = (data) => api.post('/extreme/po/bulk/generate', data);
export const bulkCloseEXPOs = (data) => api.post('/extreme/po/bulk/close', data);

// EXTREME Invoices
export const previewEXInvoice = (poId) => api.post(`/extreme/invoice/preview/${poId}`);
export const generateEXInvoice = (poId) => api.post(`/extreme/invoice/generate/${poId}`);
export const getEXInvoices = () => api.get('/extreme/invoice');
export const getEXInvoicePDF = (id) => api.get(`/extreme/invoice/${id}/pdf`, { responseType: 'blob' });
export const updateEXInvoiceStatus = (id, status) => api.put(`/extreme/invoice/${id}/status`, { status });
export const cancelEXInvoice = (id) => api.post(`/extreme/invoice/${id}/cancel`);
export const updateEXInvoiceAWB = (id, data) => api.put(`/extreme/invoice/${id}/awb`, data);
export const getEXSelectedInvoicesPDF = (ids) => api.post('/extreme/invoice/selected/pdf', { invoice_ids: ids }, { responseType: 'blob' });
export const getEXSelectedInvoicesZip = (ids) => api.post('/extreme/invoice/selected/zip', { invoice_ids: ids }, { responseType: 'blob' });
export const getEXInvoiceBatches = () => api.get('/extreme/invoice/batches');
export const getEXBatchInvoicePDF = (batchId) => api.get(`/extreme/invoice/batch/${batchId}/pdf`, { responseType: 'blob' });

// EXTREME Courier Settings
export const getEXCourierSettings = () => api.get('/extreme/courier-settings');
export const updateEXCourierSettings = (data) => api.put('/extreme/courier-settings', data);

// EXTREME Stores
export const getEXStores = () => api.get('/extreme/stores');
export const addEXStore = (data) => api.post('/extreme/stores', data);
export const updateEXStore = (name, data) => api.put(`/extreme/stores/${encodeURIComponent(name)}`, data);
export const deleteEXStore = (name) => api.delete(`/extreme/stores/${encodeURIComponent(name)}`);
export const syncEXStoresFromPOs = () => api.post('/extreme/stores/sync-from-pos');

// EXTREME Revenue Analytics
export const getEXAnalyticsOverview = () => api.get('/extreme/analytics/overview');
export const getEXAnalyticsStoreSales = (params) => api.get('/extreme/analytics/store-sales', { params });
export const getEXAnalyticsStoreTopSkus = (store) => api.get(`/extreme/analytics/store-top-skus/${encodeURIComponent(store)}`);


// EXTREME Email
export const getEXStoreContacts = () => api.get('/extreme/store-contacts');
export const sendEXDispatchEmails = (data) => api.post('/extreme/dispatch/send-emails', data);

// EXTREME Inventory Movements
export const getEXInventoryMovements = (source, limit) => api.get('/extreme/inventory/movements', { params: { source, limit } });
export const exAutoDeductExport = (data) => api.post('/extreme/inventory/auto-deduct/export', data);
export const exAutoDeductShipping = (data) => api.post('/extreme/inventory/auto-deduct/shipping', data);
export const exAutoAddRTO = (data) => api.post('/extreme/inventory/auto-add/rto', data);

// ── Wholesale Portal ──────────────────────────────────────────
export const getWholesaleProducts = () => api.get('/wholesale/products');
export const uploadWholesaleCatalogue = (formData) => api.post('/wholesale/products/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadWholesaleProductImage = (pid, formData) => api.post(`/wholesale/products/${pid}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getWholesaleOrders = () => api.get('/wholesale/orders');
export const placeWholesaleOrder = (data) => api.post('/wholesale/orders', data);
export const wholesaleOrderAction = (oid, data) => api.post(`/wholesale/orders/${oid}/action`, data);
export const editWholesaleOrder = (oid, data) => api.put(`/wholesale/orders/${oid}/edit`, data);
export const getWholesaleOrder = (oid) => api.get(`/wholesale/orders/${oid}`);
export const getWholesaleProfile = (username) => api.get(`/wholesale/profile/${username}`);
export const updateWholesaleProfile = (username, data) => api.put(`/wholesale/profile/${username}`, data);
export const lockWholesaleProfile = (username) => api.post(`/wholesale/profile/${username}/lock`);
export const unlockWholesaleProfile = (username) => api.post(`/wholesale/profile/${username}/unlock`);
export const requestWholesaleUnlock = (username) => api.post(`/wholesale/profile/${username}/request-unlock`);
export const getWholesaleConfig = () => api.get('/wholesale/config');
export const saveWholesaleConfig = (data) => api.put('/wholesale/config', data);
export const uploadWholesaleLogo = (formData) => api.post('/wholesale/config/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getWholesaleUsers = () => api.get('/wholesale/users');
export const createWholesaleUser = (data) => api.post('/wholesale/users', data);

// Wholesale Notifications
export const getWholesaleNotifications = () => api.get('/wholesale/notifications');
export const markNotificationRead = (nid) => api.post(`/wholesale/notifications/${nid}/read`);
export const markAllNotificationsRead = () => api.post('/wholesale/notifications/read-all');

// Wholesale Conversations
export const getWholesaleConversations = () => api.get('/wholesale/conversations');
export const getConversationMessages = (username) => api.get(`/wholesale/conversations/${username}/messages`);
export const sendConversationMessage = (username, data) => api.post(`/wholesale/conversations/${username}/messages`, data);
